import { characterTokenGeometry } from './character_token_geometry.js';

// Whole-cell token footprints; coordinates use original-image pixels, not screen pixels.
// See docs/spell-shapes.md. Geometry is in original-image pixels, never screen pixels.
export const FEET_PER_CELL = 5;
export const MIN_RADIUS_FEET = 5;
export const MAX_RADIUS_FEET = 120;

export function validRadius(radiusFeet) {
  return (
    Number.isInteger(radiusFeet) &&
    radiusFeet >= MIN_RADIUS_FEET &&
    radiusFeet <= MAX_RADIUS_FEET &&
    radiusFeet % FEET_PER_CELL === 0
  );
}

export function validGrid({ size, offsetX, offsetY }) {
  return Number.isFinite(size) && size >= 1 && Number.isFinite(offsetX) && Number.isFinite(offsetY);
}

export function validDirection(degrees) {
  return (
    Number.isFinite(degrees) &&
    degrees >= 0 &&
    degrees <= 360 &&
    Math.abs(degrees * 100 - Math.round(degrees * 100)) < 1e-8
  );
}

export function validAreaEffect(effect) {
  if (effect.effectType === 'radius') return validRadius(effect.radiusFeet);
  if (effect.effectType === 'cube') return validRadius(effect.sideFeet);
  return (
    isDirectionalEffect(effect) &&
    validRadius(effect.lengthFeet) &&
    (effect.effectType !== 'line' || validRadius(effect.widthFeet)) &&
    validDirection(effect.directionDegrees)
  );
}

export function isDirectionalEffect(effect) {
  return effect?.effectType === 'line' || effect?.effectType === 'cone';
}

export function directionAtPointer(origin, point) {
  if (!point || ![origin.x, origin.y, point.x, point.y].every(Number.isFinite)) return null;
  const dx = point.x - origin.x;
  const dy = point.y - origin.y;
  if (Math.hypot(dx, dy) < 0.001) return null;
  return (Math.round((((Math.atan2(dy, dx) * 180) / Math.PI + 360) % 360) * 100) / 100) % 360;
}

export function areaEffectGeometry(effect, grid, parent = null) {
  if (effect.parentId) {
    const token = parent?.type === 'character' && characterTokenGeometry(parent, grid);
    if (!token) return null;
    const geometry = areaEffectGeometry({ ...effect, parentId: null, gridColumn: 0, gridRow: 0 }, grid);
    if (!geometry) return null;
    const center = { x: token.x + token.width / 2, y: token.y + token.height / 2 };
    if (effect.effectType === 'radius') {
      geometry.width += token.width;
      geometry.height += token.height;
    }
    return {
      ...geometry,
      x: center.x - (isDirectionalEffect(effect) ? 0 : geometry.width / 2),
      y: center.y - (isDirectionalEffect(effect) ? 0 : geometry.height / 2)
    };
  }
  if (effect.effectType === 'radius') return radiusGeometry(effect, grid);
  if (
    !validAreaEffect(effect) ||
    !validGrid(grid) ||
    !Number.isSafeInteger(effect.gridColumn) ||
    !Number.isSafeInteger(effect.gridRow)
  )
    return null;
  if (effect.effectType === 'cube') {
    const side = (effect.sideFeet / FEET_PER_CELL) * grid.size;
    return {
      x: grid.offsetX + effect.gridColumn * grid.size,
      y: grid.offsetY + effect.gridRow * grid.size,
      width: side,
      height: side
    };
  }
  return {
    x: grid.offsetX + (effect.gridColumn + 0.5) * grid.size,
    y: grid.offsetY + (effect.gridRow + 0.5) * grid.size,
    width: (effect.lengthFeet / FEET_PER_CELL) * grid.size,
    height: ((effect.effectType === 'cone' ? effect.lengthFeet : effect.widthFeet) / FEET_PER_CELL) * grid.size,
    tileFootprint: tileFootprint(effect, grid.size)
  };
}

function directionAxes(degrees) {
  const radians = ((degrees % 360) * Math.PI) / 180;
  const dx = Math.cos(radians);
  const dy = Math.sin(radians);
  // Treat exact diagonal ties consistently despite trigonometric rounding.
  const majorX = Math.abs(dx) + 1e-12 >= Math.abs(dy) ? Math.sign(dx) : 0;
  const majorY = majorX === 0 ? Math.sign(dy) : 0;
  const dominant = Math.max(Math.abs(dx), Math.abs(dy));
  const slope = (-majorY * dx + majorX * dy) / dominant;
  return { majorX, majorY, slope, unit: { x: dx / dominant, y: dy / dominant } };
}

const roundCell = (value) => Math.floor(value + 0.5 + 1e-10);

function tileFootprint(effect, size) {
  const axes = directionAxes(effect.directionDegrees);
  const { majorX, majorY, slope, unit } = axes;
  const steps = effect.lengthFeet / FEET_PER_CELL;
  const rows =
    effect.effectType === 'cone' ? coneRows(steps, axes) : lineRows(steps, effect.widthFeet / FEET_PER_CELL, slope);
  const cells = rows.flat().map(({ forward, sideways }) => ({
    column: forward * majorX - sideways * majorY,
    row: forward * majorY + sideways * majorX
  }));
  const left = Math.min(...cells.map((cell) => cell.column)) - 0.5;
  const top = Math.min(...cells.map((cell) => cell.row)) - 0.5;
  const right = Math.max(...cells.map((cell) => cell.column)) + 0.5;
  const bottom = Math.max(...cells.map((cell) => cell.row)) + 0.5;
  const endDistance = rayExit(cells, unit);
  return {
    cells,
    size,
    start: { x: unit.x * size, y: unit.y * size },
    end: { x: unit.x * endDistance * size, y: unit.y * endDistance * size },
    bounds: { x: left * size, y: top * size, width: (right - left) * size, height: (bottom - top) * size }
  };
}

function lineRows(steps, lanes, slope) {
  const rows = [];

  // One cross-section per 5 ft step; the origin tile is not part of the effect.
  // Quantize the entire width together so crossing a boundary never adds a lane.
  for (let step = 1; step <= steps; step++) {
    const firstLane = roundCell(step * slope - (lanes - 1) / 2);
    const row = [];
    for (let lane = firstLane; lane < firstLane + lanes; lane++) {
      row.push({ forward: step, sideways: lane });
    }
    rows.push(row);
  }
  return rows;
}

function coneRows(steps, { slope }) {
  const rows = [];
  const bias = roundCell(slope * roundCell(slope));
  for (let step = 1; step <= steps; step++) {
    // Digital cross-sections x + round(slope*y) = step + bias cannot overlap.
    // At 0° they are side-adjacent rows; at 45° they are corner-adjacent rows.
    // Center each growing row on the exact aim, with stable half-cell ties.
    const center = (slope * (step + bias)) / (1 + slope * slope);
    const firstLane = roundCell(center - (step - 1) / 2);
    const row = [];
    for (let lane = firstLane; lane < firstLane + step; lane++) {
      row.push({ forward: step + bias - roundCell(slope * lane), sideways: lane });
    }
    rows.push(row);
  }
  return rows;
}

function rayExit(cells, unit) {
  let distance = 1;
  for (const cell of cells) {
    let enter = 0,
      exit = Infinity;
    for (const [center, direction] of [
      [cell.column, unit.x],
      [cell.row, unit.y]
    ]) {
      if (Math.abs(direction) < 1e-10) {
        if (Math.abs(center) > 0.5) exit = -Infinity;
      } else {
        const a = (center - 0.5) / direction;
        const b = (center + 0.5) / direction;
        enter = Math.max(enter, Math.min(a, b));
        exit = Math.min(exit, Math.max(a, b));
      }
    }
    if (exit >= enter) distance = Math.max(distance, exit);
  }
  return distance;
}

export function footprintContainsPoint(footprint, point) {
  const column = Math.floor(point.x / footprint.size + 0.5);
  const row = Math.floor(point.y / footprint.size + 0.5);
  return footprint.cells.some((cell) => cell.column === column && cell.row === row);
}

export function areaEffectAtPointer(point, effect, grid) {
  if (effect.effectType === 'radius') return radiusAtPointer(point, effect.radiusFeet, grid);
  if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y) || !validAreaEffect(effect) || !validGrid(grid))
    return null;
  const column = (point.x - grid.offsetX) / grid.size;
  const row = (point.y - grid.offsetY) / grid.size;
  // Cubes store their top-left grid crossing, while placement follows their center.
  // Odd tile counts center on a tile; even counts center on a grid crossing.
  const halfSide = effect.effectType === 'cube' ? effect.sideFeet / FEET_PER_CELL / 2 : 0;
  return {
    effectType: effect.effectType,
    ...effectMeasurements(effect),
    gridColumn: effect.effectType === 'cube' ? Math.round(column - halfSide) : Math.floor(column),
    gridRow: effect.effectType === 'cube' ? Math.round(row - halfSide) : Math.floor(row)
  };
}

export function defaultEffectName(effect) {
  const { radiusFeet, sideFeet, lengthFeet } = effectMeasurements(effect);
  const feet = radiusFeet ?? sideFeet ?? lengthFeet;
  return `${feet} ft ${effect.effectType}`;
}

export function radiusGeometry(effect, grid) {
  if (
    effect.effectType !== 'radius' ||
    !validRadius(effect.radiusFeet) ||
    !validGrid(grid) ||
    !Number.isSafeInteger(effect.gridColumn) ||
    !Number.isSafeInteger(effect.gridRow)
  )
    return null;

  const radius = (effect.radiusFeet / FEET_PER_CELL) * grid.size;
  return {
    x: grid.offsetX + effect.gridColumn * grid.size - radius,
    y: grid.offsetY + effect.gridRow * grid.size - radius,
    width: radius * 2,
    height: radius * 2,
    sourceGridSize: grid.size
  };
}

export function radiusAtPointer(point, radiusFeet, grid) {
  if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y) || !validGrid(grid) || !validRadius(radiusFeet))
    return null;

  return {
    effectType: 'radius',
    radiusFeet,
    gridColumn: Math.round((point.x - grid.offsetX) / grid.size),
    gridRow: Math.round((point.y - grid.offsetY) / grid.size)
  };
}

export function areaEffectData(shape) {
  if (shape.type !== 'area-effect') return {};
  const { effectType, gridColumn, gridRow } = shape;
  return {
    effectType,
    ...effectMeasurements(shape),
    gridColumn,
    gridRow,
    ...(shape.parentId ? { parentId: shape.parentId } : {})
  };
}

function effectMeasurements(effect) {
  if (effect.effectType === 'radius') return { radiusFeet: effect.radiusFeet };
  if (effect.effectType === 'cube') return { sideFeet: effect.sideFeet };
  return {
    lengthFeet: effect.lengthFeet,
    ...(effect.effectType === 'line' ? { widthFeet: effect.widthFeet } : {}),
    directionDegrees: effect.directionDegrees
  };
}
